import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosInstance from '../../axios/axiosInstance'
import { REACT_APP_AUTH_API_DEV } from '../../utils/config'
import { AppUseSelector } from '../../redux/hooks'
import { membershipNameType } from '../../utils/constant'
import { STORIES } from '../../routes/routeConstant'
import { logout } from '../../redux/actions/userAction'
import './editor.css'

type RunwayStatus = 'out' | 'urgent' | 'soon' | 'ok'

interface LevelData {
  level: string
  displayName: string
  minWords: number
  maxWords: number | null
  storiesAddedInWindow: number
  fewestStoriesLeft: number | null
  daysUntilEmpty: number | null
  learnersRunningOut: number
  status: RunwayStatus
}

interface LevelGapResponse {
  levels: LevelData[]
  recommendedLevel: string
  recommendedLevelDisplay: string
  recommendationReason: 'runway' | 'demand'
  runwayWindowDays: number
  runwayUrgentDays: number
}

interface TextAnalysis {
  uniqueWordCount: number
  level: string | null
  levelDisplay: string | null
  uniqueWordLevel?: string | null
  uniqueWordLevelDisplay?: string | null
  contentLevel?: string | null
  contentLevelDisplay?: string | null
  words: string[]
}

interface Check {
  ok: boolean
  label: string
  detail: string
}

const MIN_CHINESE_CHARS = 60

const STATUS_ORDER: RunwayStatus[] = ['out', 'urgent', 'soon']

const countChineseChars = (str: string) => {
  const matches = str.match(/[一-龥]/g)
  return matches ? matches.length : 0
}

const withArticle = (name: string) => `${/^[AEIOU]/i.test(name) ? 'an' : 'a'} ${name}`

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

const wordTarget = (level: LevelData) =>
  level.maxWords === null
    ? `${level.minWords}+ unique words`
    : level.minWords === 0
      ? `Up to ${level.maxWords} unique words`
      : `${level.minWords} to ${level.maxWords} unique words`

const runsOutIn = (days: number) => {
  if (days > 365) return 'Over a year'
  if (days > 60) return `In ${Math.round(days / 30)} months`
  return `In ${plural(days, 'day', 'days')}`
}

const runsOutLabel = (level: LevelData) => {
  if (level.daysUntilEmpty === null) return 'Not running out'
  if (level.daysUntilEmpty === 0) return 'Out of stories'
  return runsOutIn(level.daysUntilEmpty)
}

const Editor: React.FC = () => {
  const navigate = useNavigate()
  const { membership, authenticated } = AppUseSelector(({ user }) => user)
  const isTeacher = membership?.name === membershipNameType.Teacher

  const [gapData, setGapData] = useState<LevelGapResponse | null>(null)
  const [gapLoading, setGapLoading] = useState(true)
  const [gapLoaded, setGapLoaded] = useState(false)
  const [targetLevel, setTargetLevel] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [analysis, setAnalysis] = useState<TextAnalysis | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [savingSnapshot, setSavingSnapshot] = useState(false)
  const [snapshotSavedAt, setSnapshotSavedAt] = useState<number | null>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const writingRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    // Wait until SET_AUTHENTICATED has fired (authenticated becomes true)
    if (!authenticated) return
    if (!isTeacher) {
      navigate(STORIES, { replace: true })
      return
    }
    if (!gapLoaded) {
      fetchGapData()
      setGapLoaded(true)
    }
  }, [authenticated, isTeacher])

  const fetchGapData = async () => {
    try {
      setGapLoading(true)
      const response = await axiosInstance.get(`${REACT_APP_AUTH_API_DEV}teacher/level-gap?audience=standard_pro`)
      setGapData(response.data)
      setTargetLevel((current) => current || response.data.recommendedLevel)
    } catch (error) {
      console.error('Failed to load level gap data', error)
    } finally {
      setGapLoading(false)
    }
  }

  const analyzeText = useCallback(async (content: string) => {
    if (!content.trim() || countChineseChars(content) < MIN_CHINESE_CHARS) {
      setAnalysis(null)
      return
    }
    try {
      setAnalyzing(true)
      const response = await axiosInstance.post(`${REACT_APP_AUTH_API_DEV}teacher/analyze-text`, { text: content })
      setAnalysis(response.data)
    } catch (error) {
      console.error('Failed to analyze text', error)
    } finally {
      setAnalyzing(false)
    }
  }, [])

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setText(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => analyzeText(value), 1500)
  }

  const levels = gapData?.levels || []
  const levelIndex = (level: string | null | undefined) => levels.findIndex((l) => l.level === level)
  const recommended = levels.find((l) => l.level === gapData?.recommendedLevel) || null
  const target = levels.find((l) => l.level === targetLevel) || recommended

  // Other levels a paying learner is running out of, most urgent first.
  const alsoNeeded = useMemo(
    () =>
      levels
        .filter((l) => l.level !== gapData?.recommendedLevel && STATUS_ORDER.includes(l.status))
        .sort((a, b) => (a.daysUntilEmpty ?? Infinity) - (b.daysUntilEmpty ?? Infinity)),
    [gapData],
  )

  const writeAt = (level: string) => {
    setTargetLevel(level)
    writingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // What the draft needs to change to land on the target level. The final
  // level is the higher of the unique-word level and the content level, so
  // each one is checked against the target on its own.
  const checks: Check[] = useMemo(() => {
    if (!analysis || !target) return []
    const targetIdx = levelIndex(target.level)
    const finalIdx = levelIndex(analysis.level)
    const wordsIdx = levelIndex(analysis.uniqueWordLevel)
    const contentIdx = levelIndex(analysis.contentLevel)
    const count = analysis.uniqueWordCount

    const result: Check[] = [
      {
        ok: finalIdx === targetIdx,
        label: `Reads as ${analysis.levelDisplay}`,
        detail: finalIdx === targetIdx ? 'Matches the target' : finalIdx > targetIdx ? 'Too hard for the target' : 'Too easy for the target',
      },
    ]

    if (wordsIdx > targetIdx && target.maxWords !== null) {
      result.push({
        ok: false,
        label: `${count} unique words`,
        detail: `Cut ${plural(count - target.maxWords, 'word', 'words')} to reach ${target.displayName}`,
      })
    } else if (finalIdx < targetIdx) {
      result.push({
        ok: false,
        label: `${count} unique words`,
        detail: `Add ${plural(target.minWords - count, 'word', 'words')} to reach ${target.displayName}`,
      })
    } else {
      result.push({ ok: true, label: `${count} unique words`, detail: wordTarget(target) })
    }

    if (contentIdx > targetIdx) {
      result.push({
        ok: false,
        label: `Grammar and vocabulary read as ${analysis.contentLevelDisplay}`,
        detail: 'Use simpler sentence patterns and more common words',
      })
    } else if (analysis.contentLevelDisplay) {
      result.push({ ok: true, label: `Grammar and vocabulary read as ${analysis.contentLevelDisplay}`, detail: 'Within the target' })
    }

    return result
  }, [analysis, target, gapData])

  const createWrappedLines = (value: string, maxCharsPerLine: number, maxLines: number) => {
    const normalized = value.replace(/\r\n/g, '\n')
    const segments = normalized.split('\n')
    const lines: string[] = []

    segments.forEach((segment) => {
      if (!segment) {
        lines.push('')
        return
      }

      for (let i = 0; i < segment.length; i += maxCharsPerLine) {
        lines.push(segment.slice(i, i + maxCharsPerLine))
      }
    })

    if (lines.length <= maxLines) {
      return lines
    }

    const clipped = lines.slice(0, maxLines)
    clipped[maxLines - 1] = `${clipped[maxLines - 1]}...`
    return clipped
  }

  const handleSaveSnapshot = () => {
    setSavingSnapshot(true)
    try {
      const canvas = document.createElement('canvas')
      const width = 1400
      const height = 900
      const padding = 56
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        throw new Error('Canvas context unavailable')
      }

      ctx.fillStyle = '#f8f6f4'
      ctx.fillRect(0, 0, width, height)

      ctx.fillStyle = '#222525'
      ctx.font = '700 42px DM Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif'
      ctx.fillText('Maayot Story Snapshot', padding, 82)

      ctx.font = '500 22px DM Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif'
      ctx.fillStyle = '#62636b'
      const timestamp = new Date().toLocaleString()
      ctx.fillText(`Saved at ${timestamp}`, padding, 122)

      const detectedLevel = analysis?.levelDisplay || 'Pending analysis'
      const uniqueWordLevel = analysis?.uniqueWordLevelDisplay || 'N/A'
      const contentLevel = analysis?.contentLevelDisplay || 'N/A'
      const uniqueWords = analysis?.uniqueWordCount ?? 0

      ctx.fillStyle = '#ffffff'
      ctx.fillRect(padding, 156, width - padding * 2, 126)
      ctx.strokeStyle = '#ecebea'
      ctx.lineWidth = 2
      ctx.strokeRect(padding, 156, width - padding * 2, 126)

      ctx.fillStyle = '#068466'
      ctx.font = '700 26px DM Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif'
      ctx.fillText(`Detected: ${detectedLevel}${target ? ` | Target: ${target.displayName}` : ''}`, padding + 20, 202)

      ctx.fillStyle = '#62636b'
      ctx.font = '500 21px DM Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif'
      ctx.fillText(`Unique-word level: ${uniqueWordLevel}`, padding + 20, 236)
      ctx.fillText(`Content level: ${contentLevel} | ${uniqueWords} unique words`, padding + 20, 268)

      const storyLines = createWrappedLines(text.trim() || '(empty draft)', 48, 20)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(padding, 312, width - padding * 2, height - 368)
      ctx.strokeStyle = '#ecebea'
      ctx.strokeRect(padding, 312, width - padding * 2, height - 368)

      ctx.fillStyle = '#222525'
      ctx.font = '600 26px PingFang SC, Microsoft YaHei, Noto Sans SC, sans-serif'
      let y = 356
      storyLines.forEach((line) => {
        ctx.fillText(line, padding + 20, y)
        y += 28
      })

      const dataUrl = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      const fileNameTs = new Date().toISOString().replace(/[:.]/g, '-')
      link.href = dataUrl
      link.download = `story-snapshot-${fileNameTs}.png`
      link.click()

      setSnapshotSavedAt(Date.now())
    } catch (error) {
      console.error('Failed to create story snapshot', error)
    } finally {
      setSavingSnapshot(false)
    }
  }

  const chineseChars = countChineseChars(text)

  return (
    <div className='editor-page'>
      <header className='editor-header'>
        <h1 className='editor-title'>Story editor</h1>
        <div className='editor-header-actions'>
          <button className='editor-save-btn' onClick={handleSaveSnapshot} disabled={savingSnapshot}>
            {savingSnapshot ? 'Saving...' : 'Save image'}
          </button>
          <button className='editor-back-btn' onClick={() => navigate(STORIES)}>
            Back to stories
          </button>
          <button className='editor-logout-btn' onClick={() => logout()}>
            Log out
          </button>
        </div>
      </header>

      <div className='editor-content'>
        {gapLoading ? (
          <div className='editor-card editor-loading'>Loading level data...</div>
        ) : !gapData || !recommended ? (
          <div className='editor-card editor-error'>Failed to load level data.</div>
        ) : (
          <>
            <section className='editor-card editor-next'>
              <div className='editor-next-main'>
                <h2 className='editor-section-title'>Write next</h2>
                <div className='editor-next-level'>{recommended.displayName}</div>
                <div className='editor-next-target'>{wordTarget(recommended)}</div>
                {gapData.recommendationReason === 'runway' ? (
                  <ul className='editor-next-facts'>
                    <li>
                      <strong>{runsOutLabel(recommended)}</strong>
                      {recommended.daysUntilEmpty === 0
                        ? ': a paying learner has read every story at this level'
                        : ': a paying learner runs out of unread stories'}
                    </li>
                    <li>
                      <strong>{plural(recommended.learnersRunningOut, 'learner', 'learners')}</strong> out or running out
                      within {gapData.runwayUrgentDays} days
                    </li>
                    <li>
                      <strong>{recommended.storiesAddedInWindow}</strong> new {recommended.displayName} stories in the last{' '}
                      {gapData.runwayWindowDays} days
                    </li>
                  </ul>
                ) : (
                  <ul className='editor-next-facts'>
                    <li>No paying learner is running out of stories at any level</li>
                    <li>Fewest stories for the share of learners at this level</li>
                  </ul>
                )}
                <button className='editor-primary-btn' onClick={() => writeAt(recommended.level)}>
                  Write {withArticle(recommended.displayName)} story
                </button>
              </div>
              {alsoNeeded.length > 0 && (
                <div className='editor-next-also'>
                  <h3 className='editor-subtitle'>Also running low</h3>
                  {alsoNeeded.map((level) => (
                    <button key={level.level} className='editor-also-item' onClick={() => writeAt(level.level)}>
                      <span className={`editor-status-dot editor-status-dot--${level.status}`} />
                      <span className='editor-also-name'>{level.displayName}</span>
                      <span className='editor-also-detail'>
                        {level.daysUntilEmpty === 0 ? 'Out of stories' : `Runs out in ${plural(level.daysUntilEmpty ?? 0, 'day', 'days')}`}
                        {`, ${level.storiesAddedInWindow} new in ${gapData.runwayWindowDays} days`}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className='editor-card'>
              <h2 className='editor-section-title'>Stories left by level</h2>
              <div className='editor-table-wrap'>
                <table className='editor-table'>
                  <thead>
                    <tr>
                      <th>Level</th>
                      <th className='editor-num editor-col-secondary'>New in {gapData.runwayWindowDays} days</th>
                      <th className='editor-num'>Fewest left</th>
                      <th>Runs out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {levels.map((level) => (
                      <tr
                        key={level.level}
                        className={`editor-table-row ${level.level === target?.level ? 'editor-table-row--target' : ''}`}
                        onClick={() => writeAt(level.level)}
                        title={`Write ${withArticle(level.displayName)} story`}
                      >
                        <td className='editor-table-level'>{level.displayName}</td>
                        <td className='editor-num editor-col-secondary'>{level.storiesAddedInWindow}</td>
                        <td className='editor-num'>{level.fewestStoriesLeft ?? '-'}</td>
                        <td>
                          <span className='editor-status'>
                            <span className={`editor-status-dot editor-status-dot--${level.status}`} />
                            {runsOutLabel(level)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className='editor-footnote'>
                Standard and Pro learners only. Fewest left: unread stories for the learner who has read the most at that
                level. Runs out: their stories left divided by their reading pace over the last {gapData.runwayWindowDays}{' '}
                days, net of new stories.
              </p>
            </section>
          </>
        )}

        <section className='editor-card' ref={writingRef}>
          <div className='editor-writing-header'>
            <h2 className='editor-section-title'>Write your story</h2>
            {levels.length > 0 && (
              <label className='editor-target-select'>
                Target
                <select value={target?.level || ''} onChange={(e) => setTargetLevel(e.target.value)}>
                  {levels.map((level) => (
                    <option key={level.level} value={level.level}>
                      {level.displayName} ({wordTarget(level).toLowerCase()})
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className='editor-checks'>
            {analyzing ? (
              <span className='editor-muted'>Analysing...</span>
            ) : analysis ? (
              <>
                {checks.map((check) => (
                  <div key={check.label} className={`editor-check ${check.ok ? 'editor-check--ok' : 'editor-check--fix'}`}>
                    <span className='editor-check-label'>{check.label}</span>
                    <span className='editor-check-detail'>{check.detail}</span>
                  </div>
                ))}
                {snapshotSavedAt && <span className='editor-save-feedback'>Image saved</span>}
              </>
            ) : (
              <span className='editor-muted'>
                {chineseChars} of {MIN_CHINESE_CHARS} Chinese characters needed to check the level
              </span>
            )}
          </div>

          <div className='editor-writing-layout'>
            <div className='editor-writing-main'>
              <textarea
                className='editor-textarea'
                value={text}
                onChange={handleTextChange}
                placeholder={target ? `Write ${withArticle(target.displayName)} story here...` : 'Start writing your Chinese story here...'}
              />
            </div>

            {analysis && analysis.words.length > 0 && (
              <div className='editor-word-panel'>
                <h3 className='editor-word-panel-title'>Unique words ({analysis.words.length})</h3>
                <div className='editor-word-list'>
                  {analysis.words.map((w, i) => (
                    <div key={`${w}-${i}`} className='editor-word-item'>
                      <span className='editor-word-chinese'>{w}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

export default Editor
